import React, { useState } from 'react';
import { Search, ShieldOff, ShieldCheck, Clock, RefreshCw, X, History, User } from 'lucide-react';
import { Card, Button, Input, StatusPill, SystemBadge } from '../components/ui/Primitives';
import { mockProspects, mockEvents } from '../components/data/mockData';
export function ProspectDatabase() {
  const [selectedProspect, setSelectedProspect] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchField, setSearchField] = useState<'Phone' | 'Name' | 'Email' | 'ID'>('Phone');
  const [prospects, setProspects] = useState(mockProspects);
  const [activeTab, setActiveTab] = useState('Cross-Platform State');
  const [toast, setToast] = useState<string | null>(null);
  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };
  const filteredProspects = prospects.filter(p => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    switch (searchField) {
      case 'Phone':
        return p.phone.toLowerCase().includes(q);
      case 'Name':
        return `${p.firstName} ${p.lastName}`.toLowerCase().includes(q);
      case 'Email':
        return p.email.toLowerCase().includes(q);
      case 'ID':
        return p.id.toLowerCase().includes(q);
      default:
        return true;
    }
  });
  const toggleDnc = (prospectId: string, system: 'bonzo' | 'ghl' | 'five9', value: boolean) => {
    setProspects(prev => prev.map(p => {
      if (p.id === prospectId) {
        const updated = {
          ...p,
          dnc: {
            ...p.dnc,
            [system]: value
          }
        };
        if (selectedProspect?.id === prospectId) setSelectedProspect(updated);
        return updated;
      }
      return p;
    }));
    showToast(`DNC ${value ? 'set' : 'removed'} for ${system} on prospect ${prospectId}`);
  };
  const setAllDnc = () => {
    if (!selectedProspect) return;
    setProspects(prev => prev.map(p => {
      if (p.id === selectedProspect.id) {
        const updated = {
          ...p,
          dnc: {
            bonzo: true,
            ghl: true,
            five9: true
          }
        };
        setSelectedProspect(updated);
        return updated;
      }
      return p;
    }));
    showToast(`Set DNC on all platforms for ${selectedProspect.firstName}`);
  };
  return <div className="h-full flex flex-col relative" data-id="element-552">
      {toast && <div className="absolute top-4 right-4 z-50 bg-slate-800 text-white px-4 py-2 rounded-lg shadow-lg text-sm animate-in fade-in slide-in-from-top-2" data-id="element-553">
          {toast}
        </div>}
      {/* Hero Search */}
      <div className="bg-navy-900 rounded-xl p-6 mb-6 shadow-sm relative overflow-hidden shrink-0" data-id="element-554">
        <div className="absolute right-0 top-0 w-64 h-64 bg-electric-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" data-id="element-555" />
        <h1 className="text-xl font-semibold text-white mb-4 relative z-10" data-id="element-556">
          Prospect Database
        </h1>
        <div className="relative max-w-3xl z-10" data-id="element-557">
          <Search className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" data-id="element-558" />
          <Input placeholder={`Search by ${searchField}...`} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-12 h-12 text-base bg-navy-950/50 border-navy-700 text-white placeholder:text-slate-500 focus-visible:ring-electric-500" data-id="element-559" />
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1" data-id="element-560">
            {(['Phone', 'Name', 'Email', 'ID'] as const).map(f => <button key={f} onClick={() => setSearchField(f)} className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${f === searchField ? 'bg-electric-500 text-white' : 'text-slate-400 hover:text-slate-200'}`} data-id="element-561">
                {f}
              </button>)}
          </div>
        </div>
        <div className="mt-3 text-sm text-slate-400 z-10 relative" data-id="element-562">
          Showing {filteredProspects.length} results
        </div>
      </div>

      {/* Split View Container */}
      <div className="flex-1 flex gap-6 min-h-0" data-id="element-563">
        {/* Left List */}
        <Card className={`flex flex-col min-h-0 transition-all duration-300 ${selectedProspect ? 'w-[480px] shrink-0' : 'flex-1'}`} data-id="element-564">
          <div className="overflow-y-auto flex-1 p-0" data-id="element-565">
            {filteredProspects.length === 0 ? <div className="flex flex-col items-center justify-center h-full text-slate-500 p-8 text-center" data-id="element-566">
                <Search className="w-8 h-8 mb-3 text-slate-300" data-id="element-567" />
                <p className="text-sm font-medium text-slate-900" data-id="element-568">
                  No prospects found
                </p>
                <p className="text-xs mt-1" data-id="element-569">
                  Try adjusting your search query or field.
                </p>
              </div> : <table className="w-full text-left border-collapse" data-id="element-570">
                <thead className="bg-slate-50 sticky top-0 z-10 border-b border-slate-200 shadow-sm" data-id="element-571">
                  <tr data-id="element-572">
                    <th className="px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-573">
                      Prospect
                    </th>
                    <th className="px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-574">
                      Phone
                    </th>
                    <th className="px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-575">
                      DNC Status
                    </th>
                    {!selectedProspect && <>
                        <th className="px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-576">
                          Owner
                        </th>
                        <th className="px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-577">
                          Tags
                        </th>
                        <th className="px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider text-right" data-id="element-578">
                          Actions
                        </th>
                      </>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100" data-id="element-579">
                  {filteredProspects.map(p => <tr key={p.id} onClick={() => setSelectedProspect(p)} className={`hover:bg-slate-50 cursor-pointer transition-colors ${selectedProspect?.id === p.id ? 'bg-electric-50/50 border-l-2 border-l-electric-500' : 'border-l-2 border-l-transparent'}`} data-id="element-580">
                      <td className="px-4 py-3" data-id="element-581">
                        <div className="flex items-center gap-3" data-id="element-582">
                          <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-medium text-slate-600 shrink-0" data-id="element-583">
                            {p.firstName[0]}
                            {p.lastName[0]}
                          </div>
                          <div className="min-w-0" data-id="element-584">
                            <div className="text-sm font-medium text-slate-900 truncate" data-id="element-585">
                              {p.firstName} {p.lastName}
                            </div>
                            <div className="text-xs text-slate-500 truncate" data-id="element-586">
                              {p.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm font-mono text-slate-600 whitespace-nowrap" data-id="element-587">
                        {p.phone}
                      </td>
                      <td className="px-4 py-3" data-id="element-588">
                        <div className="flex gap-1" data-id="element-589">
                          <DncMiniPill system="B" active={p.dnc.bonzo} color="bg-orange-500" data-id="element-590" />
                          <DncMiniPill system="G" active={p.dnc.ghl} color="bg-teal-500" data-id="element-591" />
                          <DncMiniPill system="5" active={p.dnc.five9} color="bg-violet-500" data-id="element-592" />
                        </div>
                      </td>
                      {!selectedProspect && <>
                          <td className="px-4 py-3 text-sm text-slate-600" data-id="element-593">
                            {p.owner}
                          </td>
                          <td className="px-4 py-3" data-id="element-594">
                            <div className="flex gap-1 flex-wrap" data-id="element-595">
                              {p.tags.slice(0, 2).map((t: string) => <span key={t} className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-[10px] font-medium" data-id="element-596">
                                  {t}
                                </span>)}
                              {p.tags.length > 2 && <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-[10px] font-medium" data-id="element-597">
                                  +{p.tags.length - 2}
                                </span>}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right" data-id="element-598">
                            <div className="flex items-center justify-end gap-1" data-id="element-599">
                              <ActionIcon icon={<ShieldOff className="w-4 h-4 text-red-500" data-id="element-601" />} tooltip="Set DNC (Bonzo)" onClick={e => {
                        e.stopPropagation();
                        toggleDnc(p.id, 'bonzo', true);
                      }} data-id="element-600" />
                              <ActionIcon icon={<ShieldCheck className="w-4 h-4 text-emerald-500" data-id="element-603" />} tooltip="Remove DNC (Bonzo)" onClick={e => {
                        e.stopPropagation();
                        toggleDnc(p.id, 'bonzo', false);
                      }} data-id="element-602" />
                              <ActionIcon icon={<RefreshCw className="w-4 h-4 text-slate-400" data-id="element-605" />} tooltip="Refresh" onClick={e => {
                        e.stopPropagation();
                        showToast('Refreshing prospect data...');
                      }} data-id="element-604" />
                            </div>
                          </td>
                        </>}
                    </tr>)}
                </tbody>
              </table>}
          </div>
        </Card>

        {/* Right Detail Panel */}
        {selectedProspect && <Card className="flex-1 flex flex-col min-h-0 animate-in fade-in slide-in-from-right-4" data-id="element-606">
            <div className="p-6 border-b border-slate-200 bg-surface shrink-0" data-id="element-607">
              <div className="flex justify-between items-start" data-id="element-608">
                <div className="flex gap-4" data-id="element-609">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-electric-400 to-electric-600 flex items-center justify-center text-xl font-bold text-white shadow-sm" data-id="element-610">
                    {selectedProspect.firstName[0]}
                    {selectedProspect.lastName[0]}
                  </div>
                  <div data-id="element-611">
                    <h2 className="text-2xl font-bold text-slate-900" data-id="element-612">
                      {selectedProspect.firstName} {selectedProspect.lastName}
                    </h2>
                    <div className="flex items-center gap-4 mt-1 text-sm text-slate-600" data-id="element-613">
                      <span className="font-mono text-electric-600" data-id="element-614">
                        {selectedProspect.phone}
                      </span>
                      <span data-id="element-615">{selectedProspect.email}</span>
                    </div>
                    <div className="flex gap-2 mt-3" data-id="element-616">
                      {selectedProspect.tags.map((t: string) => <span key={t} className="px-2.5 py-1 bg-slate-100 text-slate-700 rounded-md text-xs font-medium" data-id="element-617">
                          {t}
                        </span>)}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2" data-id="element-618">
                  <Button variant="danger" size="sm" onClick={setAllDnc} data-id="element-619">
                    <ShieldOff className="w-4 h-4 mr-2" data-id="element-620" /> Set DNC All
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setSelectedProspect(null)} data-id="element-621">
                    <X className="w-4 h-4" data-id="element-622" />
                  </Button>
                </div>
              </div>
            </div>

            <div className="flex border-b border-slate-200 px-6 shrink-0 bg-slate-50/50" data-id="element-623">
              {['Cross-Platform State', 'Sync History', 'Opt-out Timeline'].map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${tab === activeTab ? 'border-electric-500 text-electric-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-id="element-624">
                    {tab}
                  </button>)}
            </div>

            <div className="p-6 overflow-y-auto flex-1 bg-slate-50/30" data-id="element-625">
              {activeTab === 'Cross-Platform State' && <>
                  <h3 className="text-sm font-semibold text-slate-800 mb-4" data-id="element-626">
                    Current DNC Status
                  </h3>
                  <div className="grid grid-cols-2 gap-4 mb-8" data-id="element-627">
                    <SystemStateCard system="bonzo" active={selectedProspect.dnc.bonzo} id="bz_89213" data-id="element-628" />
                    <SystemStateCard system="ghl" active={selectedProspect.dnc.ghl} id="ghl_contact_441" data-id="element-629" />
                    <SystemStateCard system="five9" active={selectedProspect.dnc.five9} id="f9_list_item_99" data-id="element-630" />
                    <SystemStateCard system="salesforce" active={false} id="0038c00002qX" data-id="element-631" />
                  </div>

                  <h3 className="text-sm font-semibold text-slate-800 mb-4" data-id="element-632">
                    Recent Activity
                  </h3>
                  <div className="space-y-4" data-id="element-633">
                    <ActivityRow type="sync" desc="Status synced from Bonzo to GHL" time="2 hours ago" data-id="element-634" />
                    <ActivityRow type="dnc" desc="Marked DNC in Bonzo by System" time="1 day ago" data-id="element-635" />
                    <ActivityRow type="owner" desc="Reassigned to John Doe" time="3 days ago" data-id="element-636" />
                  </div>
                </>}
              {activeTab === 'Sync History' && <div className="space-y-3" data-id="element-637">
                  <h3 className="text-sm font-semibold text-slate-800 mb-4" data-id="element-638">
                    Recent Sync Events
                  </h3>
                  {mockEvents.slice(0, 5).map(e => <div key={e.id} className="p-3 bg-white border border-slate-200 rounded-lg shadow-sm flex items-center justify-between" data-id="element-639">
                      <div className="flex items-center gap-3" data-id="element-640">
                        <StatusPill status={e.status as any} data-id="element-641" />
                        <span className="text-sm font-medium text-slate-700" data-id="element-642">
                          {e.type}
                        </span>
                      </div>
                      <span className="text-xs font-mono text-slate-500" data-id="element-643">
                        {new Date(e.timestamp).toLocaleDateString()}
                      </span>
                    </div>)}
                </div>}
              {activeTab === 'Opt-out Timeline' && <div className="space-y-4 relative before:absolute before:inset-0 before:ml-4 before:-translate-x-px before:h-full before:w-0.5 before:bg-slate-200" data-id="element-644">
                  <div className="relative flex items-center gap-4" data-id="element-645">
                    <div className="w-8 h-8 rounded-full bg-red-100 border-2 border-white flex items-center justify-center z-10 shrink-0" data-id="element-646">
                      <ShieldOff className="w-4 h-4 text-red-500" data-id="element-647" />
                    </div>
                    <div className="flex-1 bg-white p-3 rounded-lg border border-slate-200 shadow-sm" data-id="element-648">
                      <div className="text-sm font-medium text-slate-900" data-id="element-649">
                        DNC Set (Bonzo)
                      </div>
                      <div className="text-xs text-slate-500" data-id="element-650">
                        Triggered by STOP reply
                      </div>
                    </div>
                  </div>
                  <div className="relative flex items-center gap-4" data-id="element-651">
                    <div className="w-8 h-8 rounded-full bg-emerald-100 border-2 border-white flex items-center justify-center z-10 shrink-0" data-id="element-652">
                      <ShieldCheck className="w-4 h-4 text-emerald-500" data-id="element-653" />
                    </div>
                    <div className="flex-1 bg-white p-3 rounded-lg border border-slate-200 shadow-sm" data-id="element-654">
                      <div className="text-sm font-medium text-slate-900" data-id="element-655">
                        DNC Removed (GHL)
                      </div>
                      <div className="text-xs text-slate-500" data-id="element-656">
                        Manual override by Admin
                      </div>
                    </div>
                  </div>
                </div>}
            </div>
          </Card>}
      </div>
    </div>;
}
function DncMiniPill({
  system,
  active,
  color
}: {
  system: string;
  active: boolean;
  color: string;
}) {
  return <div className={`w-5 h-5 rounded flex items-center justify-center text-[10px] font-bold ${active ? `${color} text-white` : 'bg-slate-100 text-slate-400'}`} title={active ? 'DNC Active' : 'Clear'} data-id="element-657">
      {system}
    </div>;
}
function ActionIcon({
  icon,
  tooltip,
  onClick
}: {
  icon: React.ReactNode;
  tooltip: string;
  onClick?: (e: React.MouseEvent) => void;
}) {
  return <button className="p-1.5 hover:bg-slate-200 rounded-md transition-colors" title={tooltip} onClick={onClick} data-id="element-658">
      {icon}
    </button>;
}
function SystemStateCard({
  system,
  active,
  id
}: {
  system: string;
  active: boolean;
  id: string;
}) {
  return <Card className="p-4 bg-white border-slate-200" data-id="element-659">
      <div className="flex justify-between items-start mb-3" data-id="element-660">
        <SystemBadge system={system as any} data-id="element-661" />
        <StatusPill status={active ? 'error' : 'success'} dot={false} data-id="element-662" />
      </div>
      <div className="text-xs text-slate-500 mb-1" data-id="element-663">Remote ID</div>
      <div className="font-mono text-sm text-slate-700" data-id="element-664">{id}</div>
    </Card>;
}
function ActivityRow({
  type,
  desc,
  time
}: {
  type: string;
  desc: string;
  time: string;
}) {
  const icons = {
    sync: <RefreshCw className="w-4 h-4 text-electric-500" data-id="element-665" />,
    dnc: <ShieldOff className="w-4 h-4 text-red-500" data-id="element-666" />,
    owner: <User className="w-4 h-4 text-slate-500" data-id="element-667" />
  };
  return <div className="flex items-center gap-4 p-3 bg-white rounded-lg border border-slate-200 shadow-sm" data-id="element-668">
      <div className="w-8 h-8 rounded-full bg-slate-50 flex items-center justify-center border border-slate-100" data-id="element-669">
        {icons[type as keyof typeof icons]}
      </div>
      <div className="flex-1" data-id="element-670">
        <div className="text-sm font-medium text-slate-800" data-id="element-671">{desc}</div>
        <div className="text-xs text-slate-500" data-id="element-672">{time}</div>
      </div>
    </div>;
}